// src/components/practice/tasks/MatchingHeadings.jsx
import React from 'react';
import { FlagButton } from '../PartDisplay';
import { marked } from 'marked';
import MatchingQuestion from '../MatchingQuestion';
import { X } from 'lucide-react';

const MatchingHeadings = ({
  partData,
  questions,
  examples,
  partAnswers,
  handleBatchAnswerChange,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet,
  onOpenMobileAnswerSheet,
  ...props
}) => {
  const headings = partData.content?.options || [];
  const mainText = partData.content?.mainText || '';
  // --- NEW: Logic for used answers (from examples and real answers) ---
  const realAnswers = Object.values(partAnswers).filter(Boolean);
  const exampleAnswers = examples.map(ex => ex.correctAnswer);
  const usedHeadingIds = new Set([...realAnswers, ...exampleAnswers]);
  // --- END NEW ---

  // 1. Map Paragraphs (which are the "questions") - Unchanged
  const paragraphQuestions = questions
    .filter(q => !q.isExample)
    .map(q => ({
      questionId: q.questionId,
      number: q.questionText.split(' ')[0], // Gets the "§ 1" part
      text: q.questionText,
    }));

  // 2. Map Headings (which are the "options") - Unchanged
  const headingOptions = headings.map(h => ({ id: h.id, text: h.text }));

  // 3. Add the special "no match" option - Unchanged
  const specialOption = { id: partData.examProvider === 'TELC' ? 'x' : '0' };
  const allOptions = [...headingOptions, specialOption];


  // --- LEFT COLUMN CONTENT (Reading) ---
// Check if this is Goethe B2 Reading Part 5
// Check if this is Goethe B2 Reading Part 5
const isGoetheB2Part5 =
  partData?.examProvider?.toUpperCase() === 'GOETHE' &&
  partData?.level?.toUpperCase() === 'B2' &&
  (partData?.partId === 'reading-part-5' || partData?.partNumber === 5);



// --- LEFT COLUMN CONTENT (Reading) ---
const leftContent = (
  <div className="space-y-6">
    {isGoetheB2Part5 ? (
  // Two-column layout for Goethe B2 Part 5
  <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
    <div className="flex gap-6 items-start">
      {/* Left column */}
      {/* Left column */}
<div className="w-1/2 border-r border-gray-300 pr-6 flex-shrink-0">
        {/* Inhaltsverzeichnis at top of left column */}
        <div className="mb-6">
          <h3 className="text-base font-bold text-gray-900 mb-3">
            Inhaltsverzeichnis
          </h3>
          <ul className="space-y-2">
            {headings.map(h => {
              const isUsed = usedHeadingIds.has(h.id);
              return (
                <li key={h.id} className="flex items-start">
                  <span className="font-bold mr-3 min-w-[24px]">{h.id}</span>
                  <span className={`text-sm leading-relaxed ${isUsed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                    {h.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Text continues below Inhaltsverzeichnis in left column */}
        <div
          className="prose prose-sm max-w-none text-gray-900 leading-relaxed text-justify"
          style={{ columnCount: 1 }}
          dangerouslySetInnerHTML={{ __html: marked.parse(mainText.split('\n\n').slice(0, Math.floor(mainText.split('\n\n').length / 2)).join('\n\n'), { breaks: true }) }}
        />
      </div>

      {/* Right column - continuation of text */}
      <div className="w-1/2 pl-6 flex-shrink-0">
        <div
          className="prose prose-sm max-w-none text-gray-900 leading-relaxed text-justify"
          dangerouslySetInnerHTML={{ __html: marked.parse(mainText.split('\n\n').slice(Math.floor(mainText.split('\n\n').length / 2)).join('\n\n'), { breaks: true }) }}
        />
      </div>
    </div>
  </div>
) : (
      // Original single-column layout for other exams
      <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-gray-300 pb-3 mb-4">
          Studienordnung
        </h3>
        <div
          className="prose prose-sm max-w-none text-gray-900 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: marked.parse(mainText, { breaks: true }) }}
        />
      </div>
    )}
  </div>
);
  // --- RIGHT COLUMN CONTENT (Answering) ---
  const rightContent = (
    <div>
      {/* Answer Sheet Header (Unchanged) */}
      <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
        <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
          Antwortbogen
        </h3>
        <p className="text-xs text-gray-600 mt-1">
          Welche Überschrift passt zu welchem Paragraphen?
        </p>
      </div>

    {/* --- MODIFIED: Answer Bank (Headings) - Hidden for Goethe B2 Part 5 --- */}
{!isGoetheB2Part5 && (
  <div className="border-2 border-gray-900 bg-white p-4 rounded-none mb-4">
    <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
      Verfügbare Überschriften
    </h4>
    <ul className="list-none space-y-1">
          {headings.map(h => {
            const isUsed = usedHeadingIds.has(h.id); // Check if used
            return (
              <li key={h.id}>
                <span className="font-bold mr-2">{h.id.toUpperCase()})</span>
                {/* Apply strikethrough if used */}
                <span className={`text-sm ${isUsed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                  {h.text}
                </span>
              </li>
            );
          })}
          </ul>
  </div>
)}

      {/* --- MODIFIED: Matching Component Box --- */}
      <div className="border-2 border-gray-900 bg-white rounded-none">

        {/* --- NEW: Examples rendered inside the answer box --- */}
        {examples.map((ex, idx) => {
          const exampleText = ex.questionText || "Beispiel"; // e.g., "§ 0"
          const correctAdText = ex.correctAnswer ? ex.correctAnswer.toUpperCase() : '?';

          return (
            <div
              key={`ex-${idx}`}
              className="bg-yellow-50 p-4 opacity-90 border-b border-gray-300"
            >
              <div className="flex items-center justify-between">
                {/* Left side: Example Number and Text */}
                <div className="flex-1 flex items-start">
                  <span className="font-bold text-sm mr-2 pt-0.5">
                    {ex.questionNumber || '0'}.
                  </span>
                  <span className="text-sm leading-relaxed">
                    {exampleText}
                  </span>
                </div>

                {/* Right side: Disabled "Answer" */}
                <div className="ml-4 flex-shrink-0">
                  <div
                    className="border-2 border-gray-900 bg-gray-900 text-white font-bold text-sm px-3 py-1.5 rounded-none cursor-not-allowed"
                    style={{ minWidth: '70px', textAlign: 'center' }}
                  >
                    {correctAdText}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {/* Regular Questions (wrapped in p-4) */}
        <div className="p-4">
          <MatchingQuestion
            questions={paragraphQuestions}
            options={allOptions}
            savedAnswers={partAnswers}
            onAnswerChange={handleBatchAnswerChange}
            title="Überschrift wählen"
            flaggedQuestions={flaggedQuestions}
            toggleFlagQuestion={toggleFlagQuestion}
            exampleAnswers={exampleAnswers} // NEW: Pass example answers
          />
        </div>
      </div>
    </div>
  );

  const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
      <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
          <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
          <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
      </div>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
      <div className="grid grid-cols-1 md:grid-cols-5 md:gap-8">
        <div className="md:col-span-3 min-w-0">
          {leftContent}
          {onOpenMobileAnswerSheet && (
            <button
              type="button"
              onClick={onOpenMobileAnswerSheet}
              className="md:hidden w-full mt-4 py-3 px-4 bg-[#006D77] text-white font-semibold rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform touch-manipulation"
            >
              <span>Fragen beantworten</span>
              <span aria-hidden>→</span>
            </button>
          )}
        </div>
        <div className="hidden md:block md:col-span-2 mt-6 md:mt-0">
          <div className="md:sticky md:top-6">
            {rightContent}
          </div>
        </div>
      </div>
      {mobileOverlay}
    </div>
  );
};

export default MatchingHeadings;